import type { ProjectDetail } from "../types";

/**
 * Modul-skopet detail-cache. Lever for hele sesjonen (tab-en) og deles
 * mellom alle komponenter. Ingen TTL — vi invaliderer eksplisitt når noe
 * endres (via setCached / clearCached).
 *
 * Tilstrekkelig for en intern portal med få titalls prosjekter. Skal vi senere
 * ha mange tusen, bytter vi til en LRU-cache eller React Query.
 */

const cache = new Map<number, ProjectDetail>();
const inflight = new Map<number, Promise<ProjectDetail>>();

export function getCached(projectId: number): ProjectDetail | undefined {
  return cache.get(projectId);
}

export function setCached(project: ProjectDetail): void {
  cache.set(project.projectId, project);
}

export function clearCached(projectId: number): void {
  cache.delete(projectId);
}

export function clearAllCached(): void {
  cache.clear();
}

/**
 * Henter detail. Returnerer cache umiddelbart hvis den finnes, og oppdaterer
 * cache i bakgrunnen (stale-while-revalidate). Hvis ingen cache: venter på
 * nettverk.
 */
export async function fetchProject(projectId: number): Promise<ProjectDetail> {
  // Hvis vi allerede har en pågående hent for samme id — gjenbruk den
  const existing = inflight.get(projectId);
  if (existing) return existing;

  const promise = (async () => {
    const res = await fetch(`/api/projects/${projectId}`);
    if (!res.ok) {
      const body = await res.json().catch(() => ({}));
      throw new Error(body.error ?? `${res.status} ${res.statusText}`);
    }
    const data = (await res.json()) as ProjectDetail;
    cache.set(projectId, data);
    return data;
  })();

  inflight.set(projectId, promise);
  try {
    return await promise;
  } finally {
    inflight.delete(projectId);
  }
}

/**
 * Fire-and-forget. Kan kalles fra hover-handlers uten å vente på resultatet.
 * Hopper over hent hvis vi allerede har data eller pågående hent.
 */
export function prefetchProject(projectId: number): void {
  if (cache.has(projectId) || inflight.has(projectId)) return;
  fetchProject(projectId).catch(() => {
    // Stille feil — prefetch er beste-anstrengelse
  });
}
